import { useEffect, useRef, useState, useCallback } from "react";

const DEFAULT_WIDTH  = 280;
const DEFAULT_HEIGHT = 380;
const MIN_WIDTH  = 220;
const MIN_HEIGHT = 200;
const MAX_WIDTH  = 600;
const MAX_HEIGHT = 800;

export function QuestionBannerOverlay({ banners, onDismiss, participants }) {
  const feedRef    = useRef(null);
  const panelRef   = useRef(null);
  const dragRef    = useRef({ dragging: false, startX: 0, startY: 0, startLeft: 0, startTop: 0 });
  const resizeRef  = useRef({ resizing: false, edge: null, startX: 0, startY: 0, startW: 0, startH: 0, startLeft: 0, startTop: 0 });

  const [isAtBottom,    setIsAtBottom]    = useState(true);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const [collapsed,     setCollapsed]     = useState(false);
  const [isDragging,    setIsDragging]    = useState(false);
  const [isResizing,    setIsResizing]    = useState(false);

  const [position, setPosition] = useState({ top: null, left: 16 });
  const [size,     setSize]     = useState({ width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT });

  const readyBanners = banners.filter(b => !b.refining);
  const totalCount   = readyBanners.length;

  useEffect(() => {
    const bottomLeft = window.innerHeight - DEFAULT_HEIGHT - 16;
    setPosition({ top: bottomLeft, left: 16 });
  }, []);

  const scrollToBottom = useCallback(() => {
    if (feedRef.current) {
      feedRef.current.scrollTo({ top: feedRef.current.scrollHeight, behavior: 'smooth' });
    }
    setShowScrollBtn(false);
  }, []);

  const handleScroll = useCallback(() => {
    if (!feedRef.current) return;
    const { scrollHeight, scrollTop, clientHeight } = feedRef.current;
    const atBottom = scrollHeight - scrollTop - clientHeight < 60;
    setIsAtBottom(atBottom);
    setShowScrollBtn(!atBottom);
  }, []);

  useEffect(() => {
    if (isAtBottom && feedRef.current) {
      feedRef.current.scrollTo({ top: feedRef.current.scrollHeight, behavior: 'smooth' });
    } else if (!isAtBottom && readyBanners.length > 0) {
      setShowScrollBtn(true);
    }
  }, [readyBanners.length, isAtBottom]);

  const onMouseDownDrag = useCallback((e) => {
    if (e.target.closest('button') || e.target.closest('.qb-feed') || e.target.closest('.qb-resize')) return;
    e.preventDefault();
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    dragRef.current = { dragging: true, startX: e.clientX, startY: e.clientY, startLeft: rect.left, startTop: rect.top };
    setIsDragging(true);
  }, []);

  const onMouseMoveDrag = useCallback((e) => {
    if (!dragRef.current.dragging) return;
    const panel = panelRef.current;
    if (!panel) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    const newLeft = Math.min(Math.max(dragRef.current.startLeft + dx, 0), window.innerWidth  - panel.offsetWidth);
    const newTop  = Math.min(Math.max(dragRef.current.startTop  + dy, 0), window.innerHeight - panel.offsetHeight);
    setPosition({ left: newLeft, top: newTop });
  }, []);

  const onMouseUpDrag = useCallback(() => {
    dragRef.current.dragging = false;
    setIsDragging(false);
  }, []);

  const onMouseDownResize = useCallback((e, edge) => {
    e.preventDefault();
    e.stopPropagation();
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    resizeRef.current = {
      resizing: true, edge,
      startX: e.clientX, startY: e.clientY,
      startW: rect.width,  startH: rect.height,
      startLeft: rect.left, startTop: rect.top,
    };
    setIsResizing(true);
  }, []);

  const onMouseMoveResize = useCallback((e) => {
    if (!resizeRef.current.resizing) return;
    const { edge, startX, startY, startW, startH, startLeft, startTop } = resizeRef.current;
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;

    let newW = startW, newH = startH, newLeft = startLeft, newTop = startTop;

    if (edge.includes('e')) newW = Math.min(Math.max(startW + dx, MIN_WIDTH), MAX_WIDTH);
    if (edge.includes('w')) {
      newW    = Math.min(Math.max(startW - dx, MIN_WIDTH), MAX_WIDTH);
      newLeft = startLeft + (startW - newW);
    }
    if (edge.includes('s')) newH = Math.min(Math.max(startH + dy, MIN_HEIGHT), MAX_HEIGHT);
    if (edge.includes('n')) {
      newH   = Math.min(Math.max(startH - dy, MIN_HEIGHT), MAX_HEIGHT);
      newTop = startTop + (startH - newH);
    }

    setSize({ width: newW, height: newH });
    if (edge.includes('w') || edge.includes('n')) {
      setPosition({ left: newLeft, top: newTop });
    }
  }, []);

  const onMouseUpResize = useCallback(() => {
    resizeRef.current.resizing = false;
    setIsResizing(false);
  }, []);

  const onTouchStartDrag = useCallback((e) => {
    if (e.target.closest('button') || e.target.closest('.qb-feed') || e.target.closest('.qb-resize')) return;
    const touch = e.touches[0];
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    dragRef.current = { dragging: true, startX: touch.clientX, startY: touch.clientY, startLeft: rect.left, startTop: rect.top };
  }, []);

  const onTouchMoveDrag = useCallback((e) => {
    if (!dragRef.current.dragging) return;
    e.preventDefault();
    const touch = e.touches[0];
    const panel = panelRef.current;
    if (!panel) return;
    const dx = touch.clientX - dragRef.current.startX;
    const dy = touch.clientY - dragRef.current.startY;
    const newLeft = Math.min(Math.max(dragRef.current.startLeft + dx, 0), window.innerWidth  - panel.offsetWidth);
    const newTop  = Math.min(Math.max(dragRef.current.startTop  + dy, 0), window.innerHeight - panel.offsetHeight);
    setPosition({ left: newLeft, top: newTop });
  }, []);

  const onTouchEndDrag = useCallback(() => { dragRef.current.dragging = false; }, []);

  useEffect(() => {
    const onMove = (e) => { onMouseMoveDrag(e); onMouseMoveResize(e); };
    const onUp   = ()  => { onMouseUpDrag();    onMouseUpResize();    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup',   onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup',   onUp);
    };
  }, [onMouseMoveDrag, onMouseMoveResize, onMouseUpDrag, onMouseUpResize]);

  const guestCount = participants ? participants.length : 0;
  if (guestCount < 1) return null;
  if (position.top === null) return null; // wait for position init

  const isMobile   = window.innerWidth <= 480;
  const panelWidth = isMobile ? Math.min(window.innerWidth - 32, MAX_WIDTH) : size.width;

  return (
    <>
      <style>{`
        @keyframes qb-pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        @keyframes qb-slideIn { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
        .qb-feed::-webkit-scrollbar { width: 3px; }
        .qb-feed::-webkit-scrollbar-track { background: transparent; }
        .qb-feed::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); border-radius: 2px; }
        .qb-resize-e  { position:absolute; right:-4px;  top:8px;    bottom:8px;  width:8px;  cursor:ew-resize;  z-index:10; }
        .qb-resize-w  { position:absolute; left:-4px;   top:8px;    bottom:8px;  width:8px;  cursor:ew-resize;  z-index:10; }
        .qb-resize-s  { position:absolute; bottom:-4px; left:8px;   right:8px;   height:8px; cursor:ns-resize;  z-index:10; }
        .qb-resize-n  { position:absolute; top:-4px;    left:8px;   right:8px;   height:8px; cursor:ns-resize;  z-index:10; }
        .qb-resize-se { position:absolute; right:-4px;  bottom:-4px; width:14px; height:14px; cursor:se-resize; z-index:10; }
        .qb-resize-sw { position:absolute; left:-4px;   bottom:-4px; width:14px; height:14px; cursor:sw-resize; z-index:10; }
        .qb-resize-ne { position:absolute; right:-4px;  top:-4px;    width:14px; height:14px; cursor:ne-resize; z-index:10; }
        .qb-resize-nw { position:absolute; left:-4px;   top:-4px;    width:14px; height:14px; cursor:nw-resize; z-index:10; }
      `}</style>

      <div
        ref={panelRef}
        style={{
          position:        'fixed',
          top:             position.top,
          left:            position.left,
          width:           panelWidth,
          height:          collapsed ? 'auto' : size.height,
          background:      'rgba(10,10,20,0.97)',
          borderRadius:    10,
          border:          '1px solid rgba(255,255,255,0.09)',
          display:         'flex',
          flexDirection:   'column',
          zIndex:          9999,
          overflow:        'hidden',
          cursor:          isDragging ? 'grabbing' : 'default',
          userSelect:      'none',
          boxSizing:       'border-box',
        }}
        onMouseDown={onMouseDownDrag}
        onTouchStart={onTouchStartDrag}
        onTouchMove={onTouchMoveDrag}
        onTouchEnd={onTouchEndDrag}
      >
        {!collapsed && (
          <>
            <div className="qb-resize qb-resize-e"  onMouseDown={e => onMouseDownResize(e, 'e')}  />
            <div className="qb-resize qb-resize-w"  onMouseDown={e => onMouseDownResize(e, 'w')}  />
            <div className="qb-resize qb-resize-s"  onMouseDown={e => onMouseDownResize(e, 's')}  />
            <div className="qb-resize qb-resize-n"  onMouseDown={e => onMouseDownResize(e, 'n')}  />
            <div className="qb-resize qb-resize-se" onMouseDown={e => onMouseDownResize(e, 'se')} />
            <div className="qb-resize qb-resize-sw" onMouseDown={e => onMouseDownResize(e, 'sw')} />
            <div className="qb-resize qb-resize-ne" onMouseDown={e => onMouseDownResize(e, 'ne')} />
            <div className="qb-resize qb-resize-nw" onMouseDown={e => onMouseDownResize(e, 'nw')} />
          </>
        )}

        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <div style={styles.liveDot} />
            <span style={styles.headerTitle}>Questions</span>
          </div>
          <div style={styles.headerRight}>
            <span style={styles.countBadge}>
              {totalCount === 1 ? '1 question' : `${totalCount} questions`}
            </span>
            <button
              style={styles.collapseBtn}
              onClick={e => { e.stopPropagation(); setCollapsed(c => !c); }}
              title={collapsed ? 'Expand' : 'Collapse'}
              onMouseDown={e => e.stopPropagation()}
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                {collapsed
                  ? <path d="M2 4l4 4 4-4" stroke="rgba(255,255,255,0.7)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  : <path d="M2 8l4-4 4 4" stroke="rgba(255,255,255,0.7)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                }
              </svg>
            </button>
          </div>
        </div>

        {!collapsed && (
          <>
            <div
              ref={feedRef}
              className="qb-feed"
              onScroll={handleScroll}
              onMouseDown={e => e.stopPropagation()}
              onTouchStart={e => e.stopPropagation()}
              style={{
                flex:            1,
                overflowY:       'auto',
                padding:         '4px 0',
                scrollbarWidth:  'thin',
                scrollbarColor:  'rgba(255,255,255,0.1) transparent',
                minHeight:       0,
              }}
            >
              {readyBanners.length === 0 ? (
                <div style={styles.emptyState}>
                  <div style={styles.emptyIcon}>?</div>
                  <span style={styles.emptyText}>No questions yet</span>
                </div>
              ) : (
                readyBanners.map(b => (
                  <QuestionItem key={b.id} {...b} onDismiss={() => onDismiss(b.id)} />
                ))
              )}
            </div>

            <div
              style={styles.footer}
              onMouseDown={e => e.stopPropagation()}
            >
              <button
                style={styles.footerBtn}
                onClick={() => banners.forEach(b => onDismiss(b.id))}
              >
                Clear all
              </button>
              <button
                style={{ ...styles.footerBtn, ...styles.footerBtnPrimary }}
                onClick={scrollToBottom}
              >
                Latest ↓
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

function QuestionItem({ sender_name, message, refined, timestamp }) {
  const initials    = sender_name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() ?? '??';
  const color       = getAvatarColor(sender_name);
  const time        = new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const displayText = refined ?? message;

  return (
    <div style={styles.item}>
      <div style={styles.itemMeta}>
        <div style={{ ...styles.itemAvatar, background: color.bg, color: color.fg }}>
          {initials}
        </div>
        <span style={styles.itemName}>{sender_name}</span>
        <span style={styles.itemTime}>{time}</span>
      </div>
      <div style={styles.itemText}>{displayText}</div>
    </div>
  );
}

const AVATAR_COLORS = [
  { bg: 'rgba(99,57,237,0.28)',  fg: '#a78bfa' },
  { bg: 'rgba(16,185,129,0.22)', fg: '#6ee7b7' },
  { bg: 'rgba(59,130,246,0.22)', fg: '#93c5fd' },
  { bg: 'rgba(245,158,11,0.22)', fg: '#fcd34d' },
  { bg: 'rgba(236,72,153,0.2)',  fg: '#f9a8d4' },
  { bg: 'rgba(239,68,68,0.2)',   fg: '#f87171' },
];

function getAvatarColor(name = '') {
  let hash = 0;
  for (const c of name) hash += c.charCodeAt(0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

const styles = {
  header: {
    padding:         '9px 10px 9px 12px',
    borderBottom:    '1px solid rgba(255,255,255,0.07)',
    display:         'flex',
    alignItems:      'center',
    justifyContent:  'space-between',
    flexShrink:      0,
    cursor:          'grab',
  },
  headerLeft:  { display: 'flex', alignItems: 'center', gap: 6 },
  headerRight: { display: 'flex', alignItems: 'center', gap: 6 },
  liveDot: {
    width:       6,
    height:      6,
    borderRadius:'50%',
    background:  '#22c55e',
    animation:   'qb-pulse 1.8s ease-in-out infinite',
    flexShrink:  0,
  },
  headerTitle: {
    fontSize:      11,
    fontWeight:    600,
    color:         'rgba(255,255,255,0.9)',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  countBadge: {
    fontSize:   10,
    color:      'rgba(255,255,255,0.4)',
    background: 'rgba(255,255,255,0.08)',
    padding:    '2px 7px',
    borderRadius: 20,
    whiteSpace: 'nowrap',
  },
  collapseBtn: {
    background:   'rgba(255,255,255,0.06)',
    border:       '1px solid rgba(255,255,255,0.1)',
    borderRadius: 5,
    width:        22,
    height:       22,
    display:      'flex',
    alignItems:   'center',
    justifyContent:'center',
    cursor:       'pointer',
    flexShrink:   0,
    padding:      0,
  },
  emptyState: {
    display:        'flex',
    flexDirection:  'column',
    alignItems:     'center',
    justifyContent: 'center',
    gap:            8,
    opacity:        0.3,
    padding:        '32px 0',
  },
  emptyIcon: {
    width:        26,
    height:       26,
    border:       '1.5px solid rgba(255,255,255,0.4)',
    borderRadius: '50%',
    display:      'flex',
    alignItems:   'center',
    justifyContent:'center',
    fontSize:     13,
    color:        '#fff',
  },
  emptyText: { fontSize: 11, color: 'rgba(255,255,255,0.6)' },
  item: {
    padding:      '8px 12px',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
    animation:    'qb-slideIn 0.32s cubic-bezier(0.22,1,0.36,1)',
    cursor:       'default',
  },
  itemMeta: {
    display:     'flex',
    alignItems:  'center',
    gap:         6,
    marginBottom: 4,
  },
  itemAvatar: {
    width:          20,
    height:         20,
    borderRadius:   '50%',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    fontSize:       9,
    fontWeight:     700,
    flexShrink:     0,
  },
  itemName: {
    fontSize:     11,
    fontWeight:   600,
    color:        'rgba(255,255,255,0.8)',
    overflow:     'hidden',
    textOverflow: 'ellipsis',
    whiteSpace:   'nowrap',
    maxWidth:     120,
  },
  itemTime: {
    fontSize:    10,
    color:       'rgba(255,255,255,0.28)',
    marginLeft:  'auto',
    flexShrink:  0,
  },
  itemText: {
    fontSize:   12,
    color:      'rgba(255,255,255,0.88)',
    lineHeight: 1.55,
    wordBreak:  'break-word',
  },
  scrollBtn: {
    position:     'absolute',
    bottom:       52,
    right:        8,
    background:   'rgba(79,70,229,0.9)',
    color:        '#fff',
    border:       'none',
    borderRadius: 20,
    fontSize:     10,
    padding:      '4px 10px',
    cursor:       'pointer',
    zIndex:       1,
  },
  footer: {
    padding:      '7px 10px',
    borderTop:    '1px solid rgba(255,255,255,0.07)',
    display:      'flex',
    gap:          6,
    flexShrink:   0,
  },
  footerBtn: {
    flex:         1,
    fontSize:     11,
    padding:      '5px 8px',
    borderRadius: 6,
    border:       '1px solid rgba(255,255,255,0.12)',
    background:   'transparent',
    color:        'rgba(255,255,255,0.7)',
    cursor:       'pointer',
  },
  footerBtnPrimary: {
    background:   'rgba(79,70,229,0.3)',
    borderColor:  'rgba(79,70,229,0.5)',
    color:        '#a5b4fc',
  },
};